import React, { useRef } from 'react';

import lottieFile from '@/assets/lottie/onboarding_animation_events.json';
import { BottomActionLayout } from '@/components/shared/BottomActionLayout/BottomActionLayout';
import { AsaphButton } from '@/components/shared/Button';
import { UserAvatar } from '@/components/UserAvatar';
import { UserMilestones } from '@/config/constants';
import { classNames } from '@/helpers/classNames';
import { LottieReact } from '@/lib/lottie-player/esm/bundle.mjs';
import { ILottie, LottieProps } from '@/lib/lottie-player/types/react';
import { useAnalyticsEvent, useFireAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';

import { OnboardingContext } from '../Onboarding';
import styles from '../Onboarding.module.scss';
import InnerStepFive from './InnerStepFive';
import InnerStepFour from './InnerStepFour';
import InnerStepOne from './InnerStepOne';
import InnerStepThree from './InnerStepThree';
import InnerStepTwo from './InnerStepTwo';

const Lottie = LottieReact as React.ForwardRefExoticComponent<
	LottieProps & React.RefAttributes<ILottie>
>;

const StepInfographic = () => {
	const { user, updateUser } = useAuth();
	const { step, setStep } = React.useContext(OnboardingContext);
	const lottieRef = useRef<ILottie | null>(null);
	const fireComplete = useAnalyticsEvent('Onboarding_Infographic_Complete');

	useFireAnalyticsEvent({ eventName: 'Onboarding_Infographic_Step_View', extraData: { step }, deps: [step] });

	return (
		<BottomActionLayout
			className={classNames([
				styles['layout'],
				styles['step-five-layout'],
			])}
			listClassName={styles['step-one']}
			actionContent={
				<>
					{step > 9 ? (
						<AsaphButton
							color="grey"
							label="Back"
							onClick={() => {
								setStep(step - 1);
								triggerLottieEvent(
									lottieRef.current,
									step,
									true,
								);
							}}
						/>
					) : null}
					<AsaphButton
						color="blue"
						label={getLabel(step)}
						onClick={async () => {
							if (step > 11) {
								fireComplete();
								return await updateUser({
									input: {
										completedMilestones: [
											UserMilestones.PROFILE_SET_UP,
											UserMilestones.ONBOARDING_COMPLETED,
										],
									},
								});
							}
							setStep(step + 1);
							triggerLottieEvent(lottieRef.current, step + 1);
						}}
					/>
				</>
			}
		>
			<div className={styles['top-row']}>
				<UserAvatar
					size="x-large"
					userId={user.id}
					activeSize="medium"
					showActiveStatus={false}
				/>
				<Lottie
					ref={lottieRef}
					className={styles['lottie']}
					lottie={lottieFile}
				/>
			</div>
			<div className={styles['bottom-row']}>
				<RenderInnerStep step={step} />
			</div>
		</BottomActionLayout>
	);
};

export default StepInfographic;

const RenderInnerStep: React.FC<{ step: number }> = ({ step }) => {
	switch (step) {
		case 8:
			return <InnerStepOne />;
		case 9:
			return <InnerStepTwo />;
		case 10:
			return <InnerStepThree />;
		case 11:
			return <InnerStepFour />;
		case 12:
			return <InnerStepFive />;
		default:
			return <InnerStepOne />;
	}
};

const getLabel = (step: number) => {
	switch (step) {
		case 8:
			return 'Show me how';
		case 12:
			return 'Get Started';
		default:
			return 'Next';
	}
};

const triggerLottieEvent = (
	lottie: ILottie | null,
	step: number,
	reverse?: boolean,
) => {
	const eventName = `${reverse ? 'reverse_' : 'to_'}step_`;
	switch (step) {
		case 9:
			lottie?.interactivity?.trigger(`${eventName}1`);
			break;
		case 10:
			lottie?.interactivity?.trigger(`${eventName}2`);
			break;
		case 11:
			lottie?.interactivity?.trigger(`${eventName}3`);
			break;
		case 12:
			lottie?.interactivity?.trigger(`${eventName}4`);
			break;
	}
};
